/**
 * ReauthModal — AC3.
 *
 * Inline credential re-authentication modal for mid-attempt session expiry.
 *
 * Behaviour:
 * - Hidden by default.
 * - Opens when 'bimassess:session-expired' custom event is dispatched (by api-client).
 * - Shows password-only form (email is pre-filled from current session if available).
 * - On success: closes modal + dispatches 'bimassess:session-recovered'.
 * - On failure: shows error message, modal stays open.
 *
 * Fix 5: refactored to use design.md tokens via Tailwind utility classes
 * (bg-card, border-line, rounded-lg, shadow-card) + Card/Button/Input/Heading
 * primitives from @/components/ui barrel (Inv 15).
 * Inline styles removed except for z-index (not a design.md token, layout-only).
 */
import { type FormEvent, type ReactElement, useEffect, useState } from 'react';
import { Button, Card, Input, Heading } from '@/components/ui';

interface ReauthState {
  open: boolean;
  loading: boolean;
  error: string;
}

export function ReauthModal(): ReactElement {
  const [state, setState] = useState<ReauthState>({
    open: false,
    loading: false,
    error: '',
  });
  const [password, setPassword] = useState('');

  useEffect(() => {
    function onSessionExpired() {
      setState({ open: true, loading: false, error: '' });
      setPassword('');
    }

    window.addEventListener('bimassess:session-expired', onSessionExpired);
    return () => {
      window.removeEventListener('bimassess:session-expired', onSessionExpired);
    };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setState((prev) => ({ ...prev, loading: true, error: '' }));

    try {
      // NOTE: bare fetch is intentional here — this IS the auth endpoint.
      // Using apiClient (401-interceptor) would create an infinite 401-loop.
      const res = await fetch('/api/auth/sign-in', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password }),
        credentials: 'include',
      });

      const body = (await res.json()) as {
        ok?: boolean;
        error?: { message?: string };
      };

      if (!res.ok || body.ok === false) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: body.error?.message ?? 'Re-authentication failed',
        }));
        return;
      }

      // Success — close modal and notify listeners
      setState({ open: false, loading: false, error: '' });
      setPassword('');
      window.dispatchEvent(new CustomEvent('bimassess:session-recovered'));
    } catch {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Network error — please check your connection',
      }));
    }
  }

  if (!state.open) return <></>;

  return (
    // Backdrop: full-screen overlay. z-index is a layout concern (not a design token).
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Session expired — re-authenticate"
      className="fixed inset-0 flex items-center justify-center bg-ink/50 px-4"
      style={{ zIndex: 200 }}
    >
      {/* Card primitive — applies bg-card, border-line, rounded-lg, shadow-card per design.md */}
      <Card className="w-full max-w-sm p-8">
        <Heading level="h2">Session expired</Heading>
        <p className="mt-2 text-muted text-body">
          Your session has expired. Please re-enter your password to continue.
        </p>

        {state.error && (
          <p className="mt-2 text-small text-error" role="alert">
            {state.error}
          </p>
        )}

        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4">
          <Input
            id="reauth-password"
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              disabled={state.loading || !password}
            >
              {state.loading ? 'Authenticating…' : 'Re-authenticate'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
